import { AxiosResponse } from 'axios';
import React from 'react';
import { Unsubscribe } from 'redux';
import accuracyValidationKind from '../../../infrastructure/constants/accuracyValidationKind';
import actionItemHelper from '../../../infrastructure/helpers/actionItem/actionItemHelper';
import copyObject from '../../../infrastructure/helpers/common/copyObject';
import httpClient from '../../../infrastructure/helpers/common/httpClient';
import urlHelper from '../../../infrastructure/helpers/common/urlHelper';
import complianceCommentHelper from '../../../infrastructure/helpers/complianceComment/complianceCommentHelper';
import reportConfigNavigation from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation';
import actions from '../../../store/actions';
import store from '../../../store/store';
import ActionItemSaveRequest from '../../../types/common/ActionItem/ActionItemSaveRequest';
import ComplianceComment from '../../../types/common/ComplianceComment';
import locationType from '../../../types/functions/Location/LocationType';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import AccuracyValidation from '../../../types/report/AccuracyValidation';
import EditableText from '../../Common/EditableText/EditableText';
import ErrorList from '../../Common/ErrorList/ErrorList';
import AssignmentBuilder from '../../Functions/Assignment/AssignmentBuilder';
import DeclarationBuilderProps from '../../Functions/Declaration/DeclarationBuilderProps';
import CopyValidationModalState from './CopyValidationModal/CopyValidationModalState';
import { getColumns } from './ValidationBuilder.Grid';
import ValidationSection from './ValidationSection/ValidationSection';
import ObjectIndexer from '../../../infrastructure/types/ObjectIndexer';
import reportConfigComponentLock from '../../../types/report/ReportConfigComponentLock';
import { getConfirmRemoveMessage } from '../../Functions/Assignment/AssignmentItem/AssignmentItem.Helpers';
import ReportConfigComponentLock from '../../../types/report/ReportConfigComponentLock';
import ComponentType from '../../../types/report/ComponentType';
import ValidationChangeLogModalState from './ChangeLogModal/ValidationChangeLogModalState';
import ValidationChangeLogModal from './ChangeLogModal/ValidationChangeLogModal';
import identityStorage from '../../../infrastructure/authorization/identityStorage';
import ActionitemDto from '../../../types/common/ActionItem/ActionItemDto';
import WarningModal from '../../Common/Modals/WarningModal/WarningModal';
import { htmlIds } from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation.HtmlIds';
import EditSourceModalState from './EditSourceModal/EditSourceModalState';
import EditSourceModal from './EditSourceModal/EditSourceModal';
import EditInfoModalState from './EditInfoModal/EditInfoModalState';
import EditInfoModal from './EditInfoModal/EditInfoModal';
import ReportComponentStatus from '../Common/ReportComponentStatus/ReportComponentStatus';
import dataSourceKind from '../../../infrastructure/constants/dataSourceKind';
import SearchByType from '../../../types/report/SearchByType';
import copyArray from '../../../infrastructure/helpers/common/copyArray';
import ActionItemDto from '../../../types/common/ActionItem/ActionItemDto';
import assignmentHelper from '../../../infrastructure/helpers/functions/assignment/assignmentHelper';
import NameAndNumber from '../../../types/report/NameAndNumber';
import ValidationFilter from '../../../types/report/ValidationFilter';
import ReportLogicListWithPaging from '../Common/ReportLogicList/ReportLogicListWithPaging';

const getDefaultExpanded = (): ObjectIndexer<boolean> => {

    let result = {} as ObjectIndexer<boolean>;

    result[htmlIds.cnValidations] = false;
    result[htmlIds.coreValidations] = false;

    return result;
};

interface State extends DeclarationBuilderProps<AccuracyValidation> {
    expandedSections: ObjectIndexer<boolean>,
    showWarningModal: boolean,
    message: string,
    location: ReportLogicLocation,
    validation: AccuracyValidation,
    isSingle?: boolean,
    event?: React.MouseEvent<HTMLElement>,
    reportConfigComponentLock?: reportConfigComponentLock,
    searchValidationType: SearchByType,
    coreValidations: AccuracyValidation[],
    standardValidations: AccuracyValidation[],
    coreValidationsSearchNames: string[],
    standardValidationsSearchNames: string[],
    isPagingUpdate: boolean,
    navigateTo: string,
    showCoreLastPage: boolean,
    showStandardLastPage: boolean,
    showFileUploadPopup: boolean,
    validationKind: string,
    searchOptions: NameAndNumber[],
    searchBy: string
}

enum SearchBy {
    'Id' = 'Id',
    'Name' = 'Name',
    'Ref Data Source Name' = 'Ref Data Source Name',
    'Report Field Name' = 'Report Field Name',
    'Variable Name' = 'Variable Name'
}

class ValidationBuilder extends React.Component<DeclarationBuilderProps<AccuracyValidation>, State> {
    private unsubscribeCopyValidation: Unsubscribe | undefined;
    private unsubscribeSourceSave: Unsubscribe | undefined;
    private unsubscribeInfoSave: Unsubscribe | undefined;
    private unsubscribeExpandSection: Unsubscribe | undefined;
    private unsubscribeComponentChange: Unsubscribe | undefined;
    private unsubscribeNavigateTo: Unsubscribe | undefined;
    private unsubscribeNavigatedTo: Unsubscribe | undefined;

    constructor(props: DeclarationBuilderProps<AccuracyValidation>) {
        super(props);

        this.state = {
            expandedSections: getDefaultExpanded(),
            showWarningModal: false,
            message: '',
            location: {} as ReportLogicLocation,
            validation: {} as AccuracyValidation,
            isSingle: false,
            event: undefined,
            reportConfigComponentLock: {} as reportConfigComponentLock,
            searchValidationType: {} as SearchByType,
            coreValidations: this.getValidations(false),
            standardValidations: this.getValidations(true),
            coreValidationsSearchNames: [] as string[],
            standardValidationsSearchNames: [] as string[],
            isPagingUpdate: false,
            navigateTo: '',
            showFileUploadPopup: false,
            validationKind: '',
            searchOptions: [] as NameAndNumber[],
            searchBy: SearchBy.Id
        } as State;

        this.onValidationChange = this.onValidationChange.bind(this);
        this.onAddValidationClick = this.onAddValidationClick.bind(this);
        this.onRemoveValidationClick = this.onRemoveValidationClick.bind(this);
        this.onInfoClick = this.onInfoClick.bind(this);
        this.onInfoSave = this.onInfoSave.bind(this);
        this.onSourceClick = this.onSourceClick.bind(this);
        this.onSourceSave = this.onSourceSave.bind(this);
        this.onResetAssignmentClick = this.onResetAssignmentClick.bind(this);
        this.getAssignmentContent = this.getAssignmentContent.bind(this);
        this.onCommentClick = this.onCommentClick.bind(this);
        this.getCommentCount = this.getCommentCount.bind(this);
        this.onGetCommentSuccess = this.onGetCommentSuccess.bind(this);
        this.onActionItemClick = this.onActionItemClick.bind(this);
        this.onGetActionItemDataSuccess = this.onGetActionItemDataSuccess.bind(this);
        this.getActionItemCount = this.getActionItemCount.bind(this);
        this.onCopyValidationClick = this.onCopyValidationClick.bind(this);
        this.onCopyValidationSave = this.onCopyValidationSave.bind(this);
        this.tryLock = this.tryLock.bind(this);
        this.onToggleSectionClick = this.onToggleSectionClick.bind(this);
        this.isExpanded = this.isExpanded.bind(this);
        this.onLockSectionClick = this.onLockSectionClick.bind(this);
        this.onViewChangeLogClick = this.onViewChangeLogClick.bind(this);
        this.onRemoveAssignmentConfirmed = this.onRemoveAssignmentConfirmed.bind(this);
        this.expandSection = this.expandSection.bind(this);
        this.setValidationsAfterSearch = this.setValidationsAfterSearch.bind(this);
        this.onSearchInPaging = this.onSearchInPaging.bind(this);
        this.onClearSearchClick = this.onClearSearchClick.bind(this);
        this.onComponentChange = this.onComponentChange.bind(this);
        this.onReferenceSelected = this.onReferenceSelected.bind(this);
        this.onReferenceNavigatedTo = this.onReferenceNavigatedTo.bind(this);
        this.getOptions = this.getOptions.bind(this);
    }

    componentDidMount(): void {
        this.unsubscribeCopyValidation = store.subscribe(this.onCopyValidationSave);
        this.unsubscribeSourceSave = store.subscribe(this.onSourceSave);
        this.unsubscribeInfoSave = store.subscribe(this.onInfoSave);
        this.unsubscribeExpandSection = store.subscribe(this.expandSection);
        this.unsubscribeNavigateTo = store.subscribe(this.onReferenceSelected);
        this.unsubscribeComponentChange = store.subscribe(this.onComponentChange);
        this.unsubscribeNavigatedTo = store.subscribe(this.onReferenceNavigatedTo);
    }

    onReferenceNavigatedTo() {
        let appState = store.getState();

        if (appState.action !== actions.reference.navigated) {
            return;
        }

        this.setState({ navigateTo: '' });
    }

    onReferenceSelected() {
        let appState = store.getState();

        if (appState.action !== actions.reference.selected) {
            return;
        }

        let stateProperties = appState.navigateToReportConfigSection;

        if (stateProperties.expandHtmlId.split('-').includes(htmlIds.validations)) {

            let htmlIdParts = stateProperties.scrollToHtmlId.split('-');

            let isStandard = htmlIdParts[2] === locationType.statement.validationStandard;

            let validationNumber = parseInt(htmlIdParts[1]);

            this.setState({ navigateTo: stateProperties.scrollToHtmlId }, () => {

                this.onSearchInPaging([{ name: '', number: validationNumber }] as NameAndNumber[], isStandard);
            });
        }
    }

    onComponentChange() {
        let appState = store.getState();

        if (appState.action !== actions.component.change) {
            return;
        }

        if (appState.componentChange?.statement === locationType.statement.validation) {

            let standard = this.props.reportConfig.validations.filter(x => x.kind === accuracyValidationKind.standard);
            let core = this.props.reportConfig.validations.filter(x => x.kind === accuracyValidationKind.core);

            this.setState({ coreValidations: copyArray(core), standardValidations: copyArray(standard) });
        }
    }

    componentWillUnmount(): void {
        (this.unsubscribeCopyValidation as Unsubscribe)();
        (this.unsubscribeSourceSave as Unsubscribe)();
        (this.unsubscribeInfoSave as Unsubscribe)();
        (this.unsubscribeExpandSection as Unsubscribe)();
        (this.unsubscribeComponentChange as Unsubscribe)();
        (this.unsubscribeNavigateTo as Unsubscribe)();
        (this.unsubscribeNavigatedTo as Unsubscribe)();
    }

    componentDidUpdate(prevProps: DeclarationBuilderProps<AccuracyValidation>) {

        if (!this.state.isPagingUpdate) {
            if (this.props.reportConfig.validations !== prevProps.reportConfig.validations || this.props.reportConfig.validations.length !== this.props.reportConfig.validations.length) {

                let standardValidations = copyArray(this.state.standardValidations);

                let coreValidations = copyArray(this.state.coreValidations);

                let update = false;

                if (standardValidations.length > 0) {
                    this.props.reportConfig.validations.filter(v => v.kind === accuracyValidationKind.standard).forEach((val) => {
                        let standardVal = standardValidations.find(v => v.number === val.number) as AccuracyValidation;

                        if (standardVal) {
                            if (val?.errors && standardVal?.errors && standardVal.errors.length !== val.errors.length) {
                                update = true;

                                standardVal.errors = val.errors;
                            }

                            let areassignmentEqual = assignmentHelper.compareAssignments(standardVal.assignment, val.assignment);

                            if (!areassignmentEqual) {
                                update = true;

                                standardVal.assignment = val.assignment;
                            }
                        }
                    });
                }

                if (coreValidations.length > 0) {

                    this.props.reportConfig.validations.filter(v => v.kind === accuracyValidationKind.core).forEach((val) => {
                        let coreVal = coreValidations.find(v => v.number === val.number) as AccuracyValidation;

                        if (coreVal) {
                            if (val?.errors && coreVal?.errors && coreVal.errors.length !== val.errors.length) {
                                update = true;

                                coreVal.errors = val.errors;
                            }

                            let areassignmentEqual = assignmentHelper.compareAssignments(coreVal.assignment, val.assignment);

                            if (!areassignmentEqual) {
                                update = true;

                                coreVal.assignment = val.assignment;
                            }
                        }
                    });
                }

                var standardValidationsInProps = this.getValidations(true);

                var coreValidationsInProps = this.getValidations(false);

                if (standardValidationsInProps.length !== standardValidations.length) {
                    standardValidations = copyArray(standardValidationsInProps);

                    update = true;
                }

                if (coreValidationsInProps.length !== coreValidations.length) {
                    coreValidations = copyArray(coreValidationsInProps);

                    update = true;
                }

                if (update) {
                    this.setState({ standardValidations, coreValidations });
                }
            }
        }
    }

    async onValidationChange(changes: AccuracyValidation, number: number, event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): Promise<void> {

        let location = this.getLocation(number);

        let validation = this.props.reportConfig.validations.find(v => v.number === number);

        const isLocked = await this.tryLock(validation);

        if (!isLocked) {

            this.props.onDeclarationChange(location, changes);
        }
    }

    onAddValidationClick(kind: string): void {

        this.props.onAddDeclarationClick((v) => {
            v.kind = kind;

            let isStandardValidation = kind === accuracyValidationKind.standard;

            let validations = this.props.reportConfig.validations.filter(v => v.kind === kind);

            let navigateTo = reportConfigNavigation.buildValidationId(v.number, v.kind.toLowerCase());

            if (isStandardValidation) {

                this.setState({navigateTo, standardValidations: validations, showStandardLastPage: true });
            }
            else {
                this.setState({ navigateTo, coreValidations: validations, showCoreLastPage: true });
            }
        });
    }

    async onRemoveValidationClick(number: number, event?: React.MouseEvent<HTMLElement>): Promise<void> {

        let validation = this.props.reportConfig.validations.find(v => v.number === number);

        const isLocked = await this.tryLock(validation);

        if (!isLocked) {

            let location = this.getLocation(number);

            this.props.onRemoveDeclarationClick(location);
        }
    }

    async onSourceClick(number: number, event?: React.MouseEvent<HTMLElement>): Promise<void> {

        const validation = this.props.reportConfig.validations.find(v => v.number === number)

        const isLocked = await this.tryLock(validation);

        if (!isLocked) {

            let state: EditSourceModalState = {
                title: `Source for: ${validation?.identifier}`,
                isOpen: true,
                error: null,
                number: number,
                source: validation?.source ?? '',
                sourceUrl: validation?.sourceUrl ?? ''
            };

            store.dispatch({ type: actions.editSourceModal.change, payload: state });
        }
    }

    onSourceSave(): void {

        let appState = store.getState();
        if (appState.action !== actions.editSourceModal.save) {
            return;
        }

        let validation = this.props.reportConfig.validations.find(v => v.number === appState.editSourceModalSave.number);

        if (validation != null) {

            let changes = {
                source: appState.editSourceModalSave.source,
                sourceUrl: appState.editSourceModalSave.sourceUrl
            } as AccuracyValidation;

            let location = this.getLocation(appState.editSourceModalSave.number);

            this.props.onDeclarationChange(location, changes);
        }
    }

    async onInfoClick(number: number, event?: React.MouseEvent<HTMLElement>): Promise<void> {

        const validation = this.props.reportConfig.validations.find(v => v.number === number)

        const isLocked = await this.tryLock(validation);

        if (!isLocked) {

            let state: EditInfoModalState = {
                title: `CN Internal Comments for: ${validation?.identifier}`,
                isOpen: true,
                error: null,
                number: number,
                comment: validation?.internalComments ?? '',
                url: validation?.internalCommentsUrl ?? ''
            };

            store.dispatch({ type: actions.editInfoModal.change, payload: state });
        }
    }

    expandSection(): void {
        let appState = store.getState();

        if (appState.action !== actions.navigation.expand && appState.action !== actions.navigation.reportConfigSection) {
            return;
        }

        let sections = Object.assign({}, this.state.expandedSections);

        sections[appState.navigateToReportConfigSection.expandHtmlId] = true;

        this.setState({ expandedSections: sections });
    }

    onInfoSave(): void {

        let appState = store.getState();
        if (appState.action !== actions.editInfoModal.save) {
            return;
        }

        let validation = this.props.reportConfig.validations.find(v => v.number === appState.editInfoModalSave.number);

        if (validation != null) {

            let changes = {
                internalComments: appState.editInfoModalSave.comment,
                internalCommentsUrl: appState.editInfoModalSave.url
            } as AccuracyValidation;

            let location = this.getLocation(appState.editInfoModalSave.number);

            this.props.onDeclarationChange(location, changes);
        }
    }

    async onRemoveAssignmentClick(location: ReportLogicLocation, validation: AccuracyValidation, isSingle?: boolean, event?: React.MouseEvent<HTMLElement>, reportConfigComponentLock?: reportConfigComponentLock): Promise<void> {

        const isLocked = await this.tryLock(validation);

        if (!isLocked) {
            this.setState({ showWarningModal: true, message: getConfirmRemoveMessage(isSingle || false), location });
        }
    }

    onRemoveAssignmentConfirmed() {

        this.setState({ showWarningModal: false });

        this.props.onRemoveAssignmentClick(this.state.location, this.state.isSingle, this.state.event, this.state.reportConfigComponentLock);
    }

    async onResetAssignmentClick(location: ReportLogicLocation, validation: AccuracyValidation, isSingle?: boolean, event?: React.MouseEvent<HTMLElement>, reportConfigComponentLock?: reportConfigComponentLock): Promise<void> {

        const isLocked = await this.tryLock(validation);

        if (!isLocked) {

            this.props.onResetAssignmentClick(location, false, event, reportConfigComponentLock);
        }
    }

    getLocation(number: number): ReportLogicLocation {

        return { statement: locationType.statement.validation, statementNumber: number } as ReportLogicLocation;
    }

    async onLockSectionClick(event: React.MouseEvent<HTMLElement> | React.ChangeEvent<HTMLElement> | React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, number: number): Promise<boolean> {

        return await this.tryLock(undefined, number);
    }

    async tryLock(validation?: AccuracyValidation, number?: number): Promise<boolean> {

        if (this.props.onComponentContainerClick) {

            if (!validation) {

                validation = this.props.reportConfig.validations.find(v => v.number === number);
            }

            if (!validation?.isUsedByCurrentUser) {

                return await this.props.onComponentContainerClick({ componentId: validation?.id || 0, componentType: ComponentType.AccuracyValidation, number: validation?.number || 0 });
            }
        }
        return false;
    }

    async onReorderConditions(validation: AccuracyValidation, error?: string) {

        let isLocked = await this.tryLock(validation);

        if (error && !validation.errors.includes(error)) {
            validation.errors.push(error);
        }

        if (!isLocked) {
            let location = this.getLocation(validation.number);
            this.props.onDeclarationChange(location, validation);
        }
    }

    async onClearClick(location: ReportLogicLocation, validation: AccuracyValidation, conditionNumber: number, isValue?: boolean): Promise<boolean> {

        let isLocked = await this.tryLock(undefined, validation.number);

        if (!isLocked) {
            let newValidation = assignmentHelper.clearOperandsInDeclaration(location, validation, conditionNumber, isValue) as AccuracyValidation;

            this.props.onDeclarationChange(location, newValidation);
        }
        return isLocked;
    }

    getAssignmentContent(validation: AccuracyValidation): JSX.Element {

        let location = this.getLocation(validation.number);

        if (validation.isBespoke) {
            return (
                <>
                    <div className="code-block">
                        <EditableText
                            className="code"
                            multiline={true}
                            value={validation.bespokeAssignment}
                            onChange={value => this.props.onDeclarationChange(location, { bespokeAssignment: value } as AccuracyValidation)}
                            disabled={this.props.isReadOnly} />
                    </div>

                    <ErrorList errors={validation.errors} className="mt-2" />
                </>
            );
        }
        const reportConfigComponentLock: ReportConfigComponentLock = { componentId: validation.id || 0, componentType: ComponentType.AccuracyValidation, number: validation.number };

        return (
            <>
                <AssignmentBuilder
                    reportConfigType={this.props.reportConfig.type}
                    isReadOnly={validation.isReadOnly || this.props.isReadOnly}
                    location={this.getLocation(validation.number)}
                    assignment={validation.assignment}
                    fieldName="FLAG VALIDATION"
                    functions={this.props.reportConfig.userDefinedFunctions}
                    globalFunctions={this.props.collections.globalModuleConfig.userDefinedFunctions}
                    lookups={this.props.reportConfig.lookups}
                    dictionaries={this.props.reportConfig.dictionaries}
                    variables={this.props.reportConfig.variables}
                    dataSources={this.props.collections.dataSources}
                    reportFields={this.props.collections.reportFields}
                    customDataSourceFields={this.props.collections.customDataSourceFields}
                    onAddAssignmentClick={(location, event) => this.props.onAddAssignmentClick(location, event, reportConfigComponentLock)}
                    onRemoveAssignmentClick={(location, isSingle, event) => this.onRemoveAssignmentClick(location, validation, isSingle, event, reportConfigComponentLock)}
                    onResetAssignmentClick={(location, isSingle, event) => { this.onResetAssignmentClick(location, validation, isSingle, event, reportConfigComponentLock); }}
                    onLogicalOperatorChange={(location, operator, event) => this.props.onLogicalOperatorChange(location, operator, event, reportConfigComponentLock)}
                    onParenthesesChange={(location, count, type, event) => this.props.onParenthesesChange(location, count, type, event, reportConfigComponentLock)}
                    onAddConditionClick={(location, operator, event) => this.props.onAddConditionClick(location, operator, event, reportConfigComponentLock)}
                    onRemoveConditionClick={(location, event) => this.props.onRemoveConditionClick(location, event, reportConfigComponentLock)}
                    onComparisonTypeChange={(location, value, event) => this.props.onComparisonTypeChange(location, value, event, reportConfigComponentLock)}
                    onOperandClick={(async (location, event) => { this.props.onOperandClick(location, event, reportConfigComponentLock); })}
                    onCopyClick={(location) => this.props.onCopyClick(location, reportConfigComponentLock)}
                    onPasteClick={(location, event) => this.props.onPasteClick(location, event, reportConfigComponentLock)}
                    onValidateFieldClick={(location, event) => this.props.onValidateDeclarationClick(location, event, reportConfigComponentLock)}
                    allowPaste={this.props.allowPaste}
                    onClearClick={(location: ReportLogicLocation, conditionNumber: number, isValue?: boolean) => this.onClearClick(location, validation, conditionNumber, isValue)}
                    onReorderAssignmentConditions={(error) => { this.onReorderConditions(validation, error); }} />

                <ErrorList errors={validation.errors} className="mt-2" />

                <ReportComponentStatus
                    reportType={this.props.reportConfig.type}
                    lastUpdatedTime={validation.lastUpdatedTime}
                    isReadOnly={this.props.isReadOnly} />
            </ >
        );
    }

    getCommentCount(id: number): number {

        return this.props.counts.validationCommentCounts[id === null || id === undefined ? -1 : id.toString()] || 0;
    }

    async onCommentClick(id: number, event?: React.MouseEvent<HTMLElement>): Promise<any> {

        const validation = this.props.reportConfig.validations.find(v => v.id === id)

        const isLocked = await this.tryLock(validation);

        if (!isLocked) {
            let url = urlHelper.buildRoute(['validation-comment', id]);

            return httpClient.get<ComplianceComment[]>(url).then(response => this.onGetCommentSuccess(response.data, url));
        }
    }

    onGetCommentSuccess(comments: ComplianceComment[], url: string): void {

        let state = complianceCommentHelper.createModalState(comments, url);

        store.dispatch({ type: actions.complianceCommentModal.open, payload: state });
    }

    async onActionItemClick(id: number, event?: React.MouseEvent<HTMLElement>,): Promise<any> {

        const validation = this.props.reportConfig.validations.find(v => v.id === id);

        const isLocked = await this.tryLock(validation);

        if (!isLocked) {

            let identity = identityStorage.get();

            let allItemsForClient = urlHelper.buildRoute(['action-item', 'by-client-id']);

            let validationActionItems = urlHelper.buildRoute(['action-item', 'validation', id]);

            let buildRelationUrl = (actionItemId: string) => urlHelper.buildRoute(['action-item', actionItemId, 'validation', id]);

            let calls: [Promise<AxiosResponse<ActionitemDto[]>>, Promise<AxiosResponse<ActionitemDto[]>>] = [httpClient.get<ActionitemDto[]>(allItemsForClient, { reportConfigId: this.props.reportConfig.id, clientId: identity.clientId || null }), httpClient.get<ActionitemDto[]>(validationActionItems)];

            let saveRequest = { reportConfigId: this.props.reportConfig.id, validationId: id } as ActionItemSaveRequest;

            return Promise.all(calls).then(responses => this.onGetActionItemDataSuccess(responses[0].data, responses[1].data, saveRequest, buildRelationUrl));
        }
    }

    async onCopyValidationClick(number: number, event?: React.MouseEvent<HTMLElement>): Promise<void> {

        const validation = this.props.reportConfig.validations.find(v => v.number === number)

        const isLocked = await this.tryLock(validation);

        if (!isLocked) {

            let state: CopyValidationModalState = {
                title: 'Copy Validation',
                isOpen: true,
                error: null,
                sourceValidationNumber: number.toString(),
                targetValidationNumber: ''
            };

            store.dispatch({ type: actions.copyValidationModal.change, payload: state });
        }
    }

    onCopyValidationSave(): void {

        let appState = store.getState();
        if (appState.action !== actions.copyValidationModal.save) {
            return;
        }

        let sourceNumber = parseInt(appState.copyValidationModalSave.sourceValidationNumber);
        let sourceValidation = this.props.reportConfig.validations.find(x => x.number === sourceNumber) as AccuracyValidation;

        let targetNumber = parseInt(appState.copyValidationModalSave.targetValidationNumber);
        let targetValidation = this.props.reportConfig.validations.find(x => x.number === targetNumber) as AccuracyValidation;

        targetValidation.assignment = copyObject(sourceValidation.assignment);

        let location = this.getLocation(targetValidation.number);

        this.props.onDeclarationChange(location, targetValidation);
    }

    onGetActionItemDataSuccess(allActionItems: ActionItemDto[], validationActionItems: ActionitemDto[], saveRequest: ActionItemSaveRequest, buildRelationUrl: (actionItemId: string) => string): void {

        let originalActionItems: ActionItemDto[] = [];
        allActionItems.forEach((iten) => {
            originalActionItems.push(Object.assign({}, iten));
        });

        const validationItems = actionItemHelper.prepareModuleActionitems(allActionItems, validationActionItems);

        let state = actionItemHelper.createModalState(originalActionItems, allActionItems, validationItems, saveRequest, buildRelationUrl, 'AccuracyValidation');

        store.dispatch({ type: actions.actionItemModal.open, payload: state });
    }

    getActionItemCount(id: number): number {

        return this.props.counts.validationActionItemCounts[id.toString()] || 0;
    }

    onToggleSectionClick(htmlId: string): void {

        let expandedSections = this.state.expandedSections;

        expandedSections[htmlId] = !expandedSections[htmlId];

        this.setState({ expandedSections: expandedSections });
    }

    isExpanded(htmlId: string): boolean {

        return this.state.expandedSections[htmlId];
    }

    onViewChangeLogClick(id: number, event?: React.MouseEvent<HTMLElement>): void {

        const validation = this.props.reportConfig.validations.find(v => v.id === id);

        let state: ValidationChangeLogModalState = {
            title: `Change Log for Validation ID: ${validation?.identifier}`,
            isOpen: true,
            error: null,
            validationId: id
        };

        store.dispatch({ type: actions.validationChangeLogModal.open, payload: state });
    }

    onSearchBySelected(value: string, validationType: string) {

        let options: NameAndNumber[] = [];

        if (value === SearchByType.FieldName) {
            options = this.props.collections.reportFields.map((x) => { return { name: x.name, number: x.number } }) as NameAndNumber[];
            this.setState({ searchOptions: options, searchBy: value });

        }

        if (value === SearchByType.RefDataSourceName) {

            options = this.props.collections.dataSources.filter(d => d.dataSourceKind !== dataSourceKind.raw).map((x) => { return { name: x.name, number: x.id } }) as NameAndNumber[];
            this.setState({ searchOptions: options, searchBy: value });
        }

        if (value === SearchByType.VariableName) {

            options = this.props.reportConfig.variables.map((x) => { return { name: x.name, number: x.number } }) as NameAndNumber[];
            this.setState({ searchOptions: options, searchBy: value });
        }

        if (value === SearchByType.Id) {
            if (validationType === accuracyValidationKind.standard) {
                this.setState({ searchBy: value, searchOptions: this.state.standardValidations.map((x) => { return { name: x.identifier, number: x.number } }) as NameAndNumber[] });
            }
            else {
                this.setState({ searchBy: value, searchOptions: this.state.coreValidations.map((x) => { return { name: x.identifier, number: x.number } }) as NameAndNumber[] });
            }
        }

        if (value === SearchByType.Name) {
            if (validationType === accuracyValidationKind.standard) {
                this.setState({ searchBy: value, searchOptions: this.state.standardValidations.map((x) => { return { name: x.name, number: x.number } }) as NameAndNumber[] });
            }
            else {
                this.setState({ searchBy: value, searchOptions: this.state.coreValidations.map((x) => { return { name: x.name, number: x.number } }) as NameAndNumber[] });
            }
        }
    }

    getValidations(isStandard: boolean) {
        if (isStandard) {
            return this.props.reportConfig.validations.filter(x => x.kind === accuracyValidationKind.standard);
        }
        else {
            return this.props.reportConfig.validations.filter(x => x.kind === accuracyValidationKind.core);
        }
    }

    onClearSearchClick(isStandard: boolean) {

        let validations = this.getValidations(isStandard);

        if (isStandard) {
            this.setState({ standardValidations: validations, standardValidationsSearchNames: [] });
        }
        else {
            this.setState({ coreValidations: validations, coreValidationsSearchNames: [] });
        }
    }

    setValidationsAfterSearch(validations: AccuracyValidation[], isStandard: boolean): void {

        this.setState({ isPagingUpdate: true });

        if (isStandard) {
            this.setState({ standardValidations: validations });
        }
        else {
            this.setState({ coreValidations: validations });
        }
    }

    async onSearch(searchBy: string, options: NameAndNumber[], isStandard: boolean, value: string) {

        switch (searchBy) {
            case SearchByType.Id:
            case SearchByType.Name: this.onSearchInPaging(options, isStandard); break;
            default: await this.onSearchOtherTypes(isStandard ? accuracyValidationKind.standard : accuracyValidationKind.core, value, searchBy); break
        }
    }

    onSearchInPaging(options: NameAndNumber[], isStandard: boolean) {


        this.setState({ isPagingUpdate: true });

        let numbers = options.map(x => x.number);

        let kind = isStandard ? accuracyValidationKind.standard : accuracyValidationKind.core;

        let validations = this.props.reportConfig.validations.filter(x => x.kind === kind);

        let items = validations.filter(x => numbers.includes(x.number));

        if (isStandard) {
            this.setState({ standardValidations: items });
        }
        else {
            this.setState({ coreValidations: items });
        }
    }

    async onSearchOtherTypes(validationKind: string, value: string, searchBy: string): Promise<void> {

        if (validationKind === accuracyValidationKind.standard) {
            this.setState({ standardValidations: [] });
        }
        else {
            this.setState({ coreValidations: [] });
        }

        let url = 'report/validations/filter';

        let response = await httpClient.post(url, {
            validationKind,
            filterType: searchBy.split(' ').join(''),
            filterValue: value,
            reportConfig: this.props.reportConfig
        } as ValidationFilter);

        let validations: AccuracyValidation[] = response.data || [];

        validations.forEach((validation: AccuracyValidation) => {
            validation.errors = [];
        });

        await this.setValidationsAfterSearch(validations, validationKind === accuracyValidationKind.standard)
    }

    getOptions(isStandard: boolean, searchBy: string): NameAndNumber[] {

        let collection = this.props.reportConfig.validations.filter(x => isStandard ? x.kind === accuracyValidationKind.standard : x.kind === accuracyValidationKind.core);

        let options = this.state.searchOptions.length > 0 && searchBy === this.state.searchBy ? this.state.searchOptions :
            collection.map((x) => { return { name: searchBy === SearchBy.Id ? x.identifier : x.name, number: x.number } }) as NameAndNumber[];

        return options;
    }

    render(): JSX.Element {

        return (
            <>
                <ValidationSection
                    sectionId='standardValidationsSearch'
                    key={1}
                    heading="CN Validations"
                    htmlId={htmlIds.cnValidations}
                    onClick={this.onToggleSectionClick}
                    isExpanded={this.isExpanded}
                    validationKind={accuracyValidationKind.standard}
                    reportConfig={this.props.reportConfig}
                    regime={this.props.reportConfig.regime}
                    getOptions={(searchBy: string) => this.getOptions(true, searchBy)}
                    searchByOptions={Object.values(SearchBy)}
                    onSearchBySelected={(value: string) => { this.onSearchBySelected(value, accuracyValidationKind.standard); }}
                    onClearClick={() => this.onClearSearchClick(true)}
                    onSearch={(options: NameAndNumber[], value: string, searchBy: string) => this.onSearch(searchBy, options, true, value)}
                    isReadOnly={this.props.isReadOnly}>

                    <ReportLogicListWithPaging
                        showLastPage={this.state.showStandardLastPage}
                        navigateTo={this.state.navigateTo}
                        isPagingUpdate={this.state.isPagingUpdate}
                        isWaiting={this.props.isWaiting}
                        items={this.state.standardValidations}
                        columns={getColumns(
                            this.onValidationChange,
                            this.onRemoveValidationClick,
                            this.onSourceClick,
                            this.onInfoClick,
                            this.onCommentClick,
                            this.getCommentCount,
                            this.getActionItemCount,
                            this.onActionItemClick,
                            this.onCopyValidationClick,
                            this.onViewChangeLogClick,
                            this.props.isReadOnly)}
                        getContent={this.getAssignmentContent}
                        getKey={v => v.number}
                        createHtmlId={v => reportConfigNavigation.buildValidationId(v.number, v.kind.toLowerCase())}
                        onClick={(e: any, item: AccuracyValidation) => {
                            if (item.isLocked && item.isReadOnly) {
                                this.tryLock(item);
                            }
                        }} />
                    {!this.props.isReadOnly &&
                        <div className="mt-4 mb-4">
                            <button
                                onClick={() => { this.onAddValidationClick(accuracyValidationKind.standard); }}
                                className="btn btn-light">
                                + CN Validation
                            </button>
                        </div>
                    }
                </ValidationSection>

                <ValidationSection
                    sectionId='coreValidationsSearch'
                    key={2}
                    reportConfig={this.props.reportConfig}
                    heading="Core Validations"
                    htmlId={htmlIds.coreValidations}
                    onClick={this.onToggleSectionClick}
                    isExpanded={this.isExpanded}
                    validationKind={accuracyValidationKind.core}
                    regime={this.props.reportConfig.regime}
                    getOptions={(searchBy: string) => this.getOptions(false, searchBy)}
                    searchByOptions={Object.values(SearchBy)}
                    onSearchBySelected={(value: string) => { this.onSearchBySelected(value, accuracyValidationKind.core); }}
                    onClearClick={() => this.onClearSearchClick(false)}
                    onSearch={(options: NameAndNumber[], value: string, searchBy: string) => this.onSearch(searchBy, options, false, value)}
                    isReadOnly={this.props.isReadOnly}>

                    <ReportLogicListWithPaging
                        showLastPage={this.state.showCoreLastPage}
                        navigateTo={this.state.navigateTo}
                        isPagingUpdate={this.state.isPagingUpdate}
                        isWaiting={this.props.isWaiting}
                        items={this.state.coreValidations}
                        columns={getColumns(
                            this.onValidationChange,
                            this.onRemoveValidationClick,
                            this.onSourceClick,
                            this.onInfoClick,
                            this.onCommentClick,
                            this.getCommentCount,
                            this.getActionItemCount,
                            this.onActionItemClick,
                            this.onCopyValidationClick,
                            this.onViewChangeLogClick,
                            this.props.isReadOnly)}
                        getContent={this.getAssignmentContent}
                        getKey={v => v.number}
                        createHtmlId={v => reportConfigNavigation.buildValidationId(v.number, v.kind.toLowerCase())}
                        onClick={(e: React.MouseEvent<HTMLElement>, item: AccuracyValidation) => {
                            if (item.isLocked && item.isReadOnly) {
                                this.tryLock(item);
                            }
                        }}
                    />
                    {
                        !this.props.isReadOnly &&
                        <div className="mt-4 mb-4">
                            <button
                                onClick={() => { this.onAddValidationClick(accuracyValidationKind.core); }}
                                className="btn btn-light">
                                + Core Validation
                            </button>
                        </div>
                    }
                </ValidationSection>

                <ValidationChangeLogModal />

                <EditSourceModal />

                <EditInfoModal />

                {this.state.showWarningModal &&
                    <WarningModal
                        onOkClick={this.onRemoveAssignmentConfirmed}
                        onCancelClick={() => this.setState({ showWarningModal: false })}
                        title='Remove Assignment'
                        message={this.state.message} />
                }
            </>
        );
    }
}

export default ValidationBuilder;
